import { Suspense, forwardRef, lazy, useCallback, useEffect, useImperativeHandle, useRef } from "react";

import type { Control, FieldErrors } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";

import { type LevelFormMethodsHandlers } from "shared/Components/TournamentsLevelsModal";
import { useCheckIsEndDateValid } from "shared/hooks";
import { CompetitionTypes, TieBreakerEnums } from "shared/types";
import type {
	AmountSettingTypes,
	PartialPaymentSettingsModel,
	SplitPaymentSettingsModel
} from "shared/types/PaymentModel";
import type { CreateCompetitionObject } from "shared/types/SetupSeasonModel";
import { CompetitionOpenRegistrationTypes, CompetitionRegistrationTypes } from "shared/types/SetupSeasonModel";

import type { DefineDivisionsFormMethodsHandlers } from "../../Components/DefineDivisionsModal";
// import type { DefineWaiversFormMethodsHandlers } from "../../Components/DefineWaiversModal";
import type { TournamentFeeFormMethodsHandlers } from "../../Components/TournamentFeeModal";

const ChooseCompetitionType = lazy(() => import("../CreateLeagueCompetitionModal/ChooseCompetitionType"));
const CreateCompetition = lazy(() => import("shared/Components/CreateCompetition"));
const DefineCompetitionSettings = lazy(() => import("shared/Components/DefineCompetitionSettings"));
const TournamentsLevelsModal = lazy(() => import("shared/Components/TournamentsLevelsModal"));
const TournamentFeeModal = lazy(() => import("shared/Components/TournamentFeeModal"));
const DefineDivisionsModal = lazy(() => import("shared/Components/DefineDivisionsModal"));
// const DefineWaiversModal = lazy(() => import("shared/Components/DefineWaiversModal"));

interface SetupLeagueCompetitionFormMethods {
	isValid: () => boolean;
	getValues: () => Partial<CreateCompetitionObject> | void;
	triggerForm: () => void;
}

interface SetupLeagueCompetitionProps {
	season?: CreateCompetitionObject;
	step: number;
	leagueId: string;
}

export interface CreateCompetitionStepsProps {
	control: Control<Partial<CreateCompetitionObject>, any>;
	errors: FieldErrors<Partial<CreateCompetitionObject>>;
}

const SetupLeagueCompetition = forwardRef<SetupLeagueCompetitionFormMethods, SetupLeagueCompetitionProps>(
	({ season, step, leagueId }, ref) => {
		const { control, getValues, formState, setValue, setError, trigger } = useForm<Partial<CreateCompetitionObject>>({
			mode: "onChange",
			defaultValues: season || {
				name: "",
				tieBreakerRules: Object.keys(TieBreakerEnums).map((key, ord) => ({
					ord,
					type: key as TieBreakerEnums,
					settings: key === TieBreakerEnums.GOAL_DIFFERENTIAL ? { maxGoalDifferential: 5 } : undefined
				})),
				type: CompetitionTypes.SEASON,
				autoPublishAdvancements: true,
				registrationType: CompetitionRegistrationTypes.OPEN,
				openRegistrationType: CompetitionOpenRegistrationTypes.TEAM
			}
		});

		const { errors, isValid } = formState;

		const [startsOn, endsOn, type, registrationFee, customFees] = useWatch({
			control,
			name: ["startsOn", "endsOn", "type", "registrationFee", "customFees"]
		});

		const isValidEndDate = useCheckIsEndDateValid({
			startDate: startsOn!,
			endDate: endsOn!
		});

		const levelFormRef = useRef<LevelFormMethodsHandlers | null>(null);
		const createDivisionsFormRef = useRef<DefineDivisionsFormMethodsHandlers | null>(null);
		// const createWaiversFormRef = useRef<DefineWaiversFormMethodsHandlers | null>(null);
		const tournamentFeeFormRef = useRef<TournamentFeeFormMethodsHandlers | null>(null);

		useEffect(() => {
			if (!isValidEndDate) {
				setError("endsOn", { message: "End date must be after start date" });
			}
		}, [isValidEndDate, setError]);

		useImperativeHandle(ref, () => ({
			isValid: () => isValid,
			getValues: () => {
				const data = getValues();

				if (step === 4) {
					const levels = levelFormRef.current?.getInfo();

					if (!levels?.isValid) {
						levelFormRef.current?.triggerForm();
						return;
					}

					setValue("levels", levels?.levels);

					return {
						...data,
						levels: levels?.levels
					};
				}

				if (step === 5) {
					const tournamentFee = tournamentFeeFormRef.current?.getInfo();

					if (!tournamentFee?.isValid) {
						return tournamentFeeFormRef.current?.triggerForm();
					}

					let partialPaymentSettings: null | PartialPaymentSettingsModel = null;
					const partialPayment = tournamentFee.data.partialPaymentSettings;
					if (partialPayment?.partialPayment) {
						setValue("partialPayment", partialPayment?.partialPayment ? partialPayment.upfrontAmount! : null);
						partialPaymentSettings = partialPayment?.partialPayment ? partialPayment.upfrontAmount! : null;
					}

					let splitPaymentsSettings: SplitPaymentSettingsModel[] = [];
					const splitPayments = tournamentFee.data.splitPaymentSettings;
					if (splitPayments?.splitPayment) {
						splitPaymentsSettings = splitPayments.transfers!.map(tr => ({
							label: tr.label,
							entityId: tr.organization.entityId,
							entityType: tr.organization.entityType!,
							split: tr.split as {
								type: AmountSettingTypes;
								value: number;
							}
						}));
					}

					const customFees = tournamentFee.data.customFees.map((fee, i) => ({
						...fee,
						ord: i,
						ageFrom: !Number.isNaN(fee.ageFrom) ? fee.ageFrom : undefined,
						ageTo: !Number.isNaN(fee.ageTo) ? fee.ageTo : undefined,
						gender: fee.gender ?? undefined,
						level: fee.level ?? undefined
					}));

					setValue("registrationFee", tournamentFee.data.registrationFee);
					setValue("registrationFeeCurrency", tournamentFee.data.registrationFeeCurrency);
					setValue("feePaidBy", tournamentFee.data.feePaidBy);
					setValue("feeTaxPaidByClient", tournamentFee.data.feeTaxPaidByClient);
					setValue("partialPayment", partialPaymentSettings);
					setValue("splitPayment", splitPaymentsSettings);
					setValue("enableCustomFees", tournamentFee.data.enableCustomFees);
					setValue("customFees", customFees);

					return {
						...data,
						registrationFee: tournamentFee.data.registrationFee,
						registrationFeeCurrency: tournamentFee.data.registrationFeeCurrency,
						feePaidBy: tournamentFee.data.feePaidBy,
						feeTaxPaidByClient: tournamentFee.data.feeTaxPaidByClient,
						partialPayment: partialPaymentSettings,
						splitPayment: splitPaymentsSettings,
						enableCustomFees: tournamentFee.data.enableCustomFees,
						customFees: customFees
					};
				}

				if (step === 6) {
					const divisions = createDivisionsFormRef.current?.getInfo();

					if (!divisions?.isValid) {
						createDivisionsFormRef.current?.triggerForm();
						return;
					}

					setValue("divisions", divisions.divisions);

					return {
						...data,
						divisions: divisions.divisions
					};
				}

				// if (step === 7) {
				// 	const waivers = createWaiversFormRef.current?.getInfo();
				//
				// 	if (!waivers?.isValid) {
				// 		createWaiversFormRef.current?.triggerForm();
				// 		return;
				// 	}
				//
				// 	setValue("waivers", waivers.waivers);
				//
				// 	return {
				// 		...data,
				// 		waivers: waivers.waivers
				// 	};
				// }

				return data;
			},
			triggerForm: () => {
				trigger();
			}
		}));

		const getDefaultFeeSettings = useCallback(() => {
			const partialPaymentSettings = getValues("partialPayment");
			const splitPaymentSettings = getValues("splitPayment") || [];

			return {
				registrationFee: getValues("registrationFee") ?? 0,
				enableCustomFees: getValues("enableCustomFees") || false,
				feePaidBy: getValues("feePaidBy") || null,
				customFees: getValues("customFees") || [],
				partialPaymentSettings: {
					partialPayment: !!partialPaymentSettings?.type && !!partialPaymentSettings?.value,
					upfrontAmount:
						!!partialPaymentSettings?.type && !!partialPaymentSettings?.value ? partialPaymentSettings! : undefined
				},
				splitPaymentSettings: {
					splitPayment: !!splitPaymentSettings?.length,
					transfers: splitPaymentSettings.map(x => ({
						label: x.label,
						organization: {
							entityId: x.entityId,
							entityType: x.entityType
						},
						split: x.split!
					}))
				}
			};
		}, [getValues]);

		return (
			<>
				{step === 1 && (
					<Suspense>
						<ChooseCompetitionType errors={errors} control={control} />
					</Suspense>
				)}
				{step === 2 && (
					<Suspense>
						<CreateCompetition type={type!} control={control} setValue={setValue} errors={errors} />
					</Suspense>
				)}
				{step === 3 && (
					<Suspense>
						<DefineCompetitionSettings setValue={setValue} control={control} errors={errors} />
					</Suspense>
				)}
				{step === 4 && (
					<Suspense>
						<TournamentsLevelsModal ref={levelFormRef} defaultValues={getValues("levels")} />
					</Suspense>
				)}
				{step === 5 && (
					<Suspense>
						<TournamentFeeModal
							ref={tournamentFeeFormRef}
							levels={getValues("levels")!}
							openRegistrationType={getValues("openRegistrationType")}
							leagueId={leagueId}
							defaultValues={getDefaultFeeSettings()}
						/>
					</Suspense>
				)}
				{step === 6 && (
					<Suspense>
						<DefineDivisionsModal
							ref={createDivisionsFormRef}
							levels={getValues("levels")!}
							registrationFee={registrationFee || 0}
							customFees={customFees}
						/>
					</Suspense>
				)}
				{/*{step === 7 && (*/}
				{/*	<Suspense>*/}
				{/*		<DefineWaiversModal ref={createWaiversFormRef} />*/}
				{/*	</Suspense>*/}
				{/*)}*/}
			</>
		);
	}
);

export default SetupLeagueCompetition;
